/**
 * Quote funnel A/B test analytics utilities.
 *
 * NO PII: Never send name, email, phone, photos, or free-text fields.
 * NO SENSITIVE DATA: Never send medical history switch states.
 *
 * Safe to track: variant, step names, procedure counts/slugs, base prices.
 * Ensures privacy while maintaining conversion tracking.
 */

import { trackEvent as trackFirstPartyEvent } from "./clientEvents";

type QuoteABVariant = string;

export type QuoteAnalyticsEvent =
  | "quote_ab_assigned"
  | "quote_draft_started"
  | "quote_procedure_selected"
  | "quote_procedure_removed"
  | "quote_procedure_info_opened"
  | "quote_procedure_video_played"
  | "quote_procedure_cta_clicked"
  | "quote_procedure_cta_disabled_clicked"
  | "quote_contact_cta_disabled_clicked"
  | "quote_help_choose_opened"
  | "quote_procedure_type_selected"
  | "quote_procedure_type_removed"
  | "quote_simplified_procedure_cta_clicked"
  | "quote_step_view"
  | "quote_step_complete"
  | "quote_contact_submit"
  | "quote_potential_price_view"
  | "quote_final_estimate_cta_click"
  | "quote_viewport_snapshot"
  | "financing_forward_exposure"
  | "financing_apply_click"
  | "quote_focus_mode_entered"
  | "quote_focus_fallback"
  | "quote_focus_ba_clicked"
  | "quote_book_now_clicked"
  | "quote_booking_page_view"
  | "quote_booking_terms_accepted"
  | "quote_booking_payment_started"
  | "quote_photo_upload_started"
  | "quote_photo_upload_completed"
  | "quote_photo_upload_failed"
  | "quote_booking_link_state"
  | "quote_booking_callback_requested";

export type QuoteAnalyticsPayload = {
  [key: string]: any;
};

/**
 * Track quote funnel events using gtag if available, else console.log in dev.
 *
 * @param eventName - Event identifier (no spaces, lowercase_with_underscores)
 * @param payload - Event data (NO PII, NO sensitive medical data)
 */
export function trackQuoteEvent(
  eventName: QuoteAnalyticsEvent,
  payload: QuoteAnalyticsPayload,
): void {
  // Log to gtag if available. Wrapped so a broken GTM container or an
  // extension-stubbed window.gtag can never abort the caller — several call
  // sites sit directly on the booking/payment click path.
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, payload);
    } else {
      // Fallback to console in development
      console.log(`[Quote Event] ${eventName}:`, payload);
    }
  } catch {
    /* never let analytics break the app */
  }
  // Dual-write into first-party Mongo events. Wrapped so a tracker bug or
  // ad-blocker on the gtag path can never bring the funnel down.
  try {
    trackFirstPartyEvent(eventName, payload);
  } catch {
    /* never let analytics break the app */
  }
}

/**
 * Track A/B variant assignment (50/50).
 * Called once when variant is first assigned.
 */
export function trackQuoteABAssigned(variant: QuoteABVariant): void {
  trackQuoteEvent("quote_ab_assigned", {
    variant,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track draft quote creation (quote flow started).
 */
export function trackQuoteDraftStarted(
  variant: QuoteABVariant,
  proceduresCount: number = 0,
  procedureSlugs: string[] = [],
): void {
  trackQuoteEvent("quote_draft_started", {
    variant,
    procedures_count: proceduresCount,
    procedure_ids: procedureSlugs.join(","),
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureSelected(payload: {
  procedureId: string;
  procedureTypeIds?: string[];
  selectedCountAfter: number;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_selected", {
    procedure_id: payload.procedureId,
    procedure_type_ids: payload.procedureTypeIds?.join(",") || "",
    selected_count_after: payload.selectedCountAfter,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureRemoved(payload: {
  procedureId: string;
  procedureTypeIds?: string[];
  selectedCountAfter: number;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_removed", {
    procedure_id: payload.procedureId,
    procedure_type_ids: payload.procedureTypeIds?.join(",") || "",
    selected_count_after: payload.selectedCountAfter,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureInfoOpened(payload: {
  procedureId: string;
  procedureTypeIds?: string[];
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_info_opened", {
    procedure_id: payload.procedureId,
    procedure_type_ids: payload.procedureTypeIds?.join(",") || "",
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

/**
 * A patient started playback of a procedure video episode inside the
 * ProcedureStory surface (the editorial replacement for the old ⓘ info modal).
 * Fires on every `play` event, so resumes after a pause count too — the metric
 * we care about is "did the episode get watched at all", not unique starts.
 * Episode titles are editorial marketing copy, never PII.
 */
export function trackQuoteProcedureVideoPlayed(payload: {
  procedureId: string;
  episodeOrder: number;
  episodeTitle: string;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_video_played", {
    procedure_id: payload.procedureId,
    episode_order: payload.episodeOrder,
    episode_title: payload.episodeTitle,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureCtaClicked(
  selectedCount: number,
  procedureIds: string[] = [],
  // Only the focused-campaign treatment passes this; its presence IS the
  // focus-mode signal (focus_mode: false never occurs by design).
  focusSurvived?: boolean,
): void {
  trackQuoteEvent("quote_procedure_cta_clicked", {
    selected_count: selectedCount,
    procedure_ids: procedureIds.join(","),
    ...(focusSurvived !== undefined
      ? { focus_mode: true, focus_survived: focusSurvived }
      : {}),
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureCtaDisabledClicked(): void {
  trackQuoteEvent("quote_procedure_cta_disabled_clicked", {
    selected_count: 0,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Contact step's disabled "Get my free evaluation" tap (2026-09-13). Sibling of
 * trackQuoteProcedureCtaDisabledClicked above — the contact step is the one
 * that creates the lead, so a dead tap here is the most expensive one in the
 * funnel.
 *
 * NO PII: `missingFields` carries FIELD NAMES ONLY (stable machine keys such as
 * "email" / "phone" / "hipaaAgreement"), never the values the patient typed.
 * Joined into a comma string to match the shape every other list payload in
 * this module uses (procedure_ids, procedure_type_ids).
 */
export function trackQuoteContactCtaDisabledClicked(
  missingFields: string[],
): void {
  trackQuoteEvent("quote_contact_cta_disabled_clicked", {
    missing_fields: missingFields.join(","),
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteHelpChooseOpened(surface: "mobile" | "desktop"): void {
  trackQuoteEvent("quote_help_choose_opened", {
    surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureTypeSelected(payload: {
  procedureTypeId: string;
  selectedCountAfter: number;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_type_selected", {
    procedure_type_id: payload.procedureTypeId,
    selected_count_after: payload.selectedCountAfter,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteProcedureTypeRemoved(payload: {
  procedureTypeId: string;
  selectedCountAfter: number;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_procedure_type_removed", {
    procedure_type_id: payload.procedureTypeId,
    selected_count_after: payload.selectedCountAfter,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

export function trackQuoteSimplifiedProcedureCtaClicked(
  selectedCount: number,
  procedureTypeIds: string[] = [],
): void {
  trackQuoteEvent("quote_simplified_procedure_cta_clicked", {
    selected_count: selectedCount,
    procedure_type_ids: procedureTypeIds.join(","),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track step view (when user navigates to a step).
 *
 * @param variant - "control" or "preview"
 * @param stepName - e.g., "procedures", "preview_range", "contact", "potential_price", "photos"
 * @param proceduresCount - Number of selected procedures
 * @param procedureSlugs - Comma-separated procedure IDs (e.g., "proc1,proc2,proc3")
 */
export function trackQuoteStepView(
  variant: QuoteABVariant,
  stepName: string,
  proceduresCount: number = 0,
  procedureSlugs: string[] = [],
): void {
  trackQuoteEvent("quote_step_view", {
    variant,
    step: stepName,
    procedures_count: proceduresCount,
    procedure_ids: procedureSlugs.join(","),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track step completion (when user clicks next/submit on a step).
 */
export function trackQuoteStepComplete(
  variant: QuoteABVariant,
  stepName: string,
  proceduresCount: number = 0,
  procedureSlugs: string[] = [],
): void {
  trackQuoteEvent("quote_step_complete", {
    variant,
    step: stepName,
    procedures_count: proceduresCount,
    procedure_ids: procedureSlugs.join(","),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track contact form submission (NO email/phone/name).
 */
export function trackQuoteContactSubmit(
  variant: QuoteABVariant,
  proceduresCount: number = 0,
  procedureSlugs: string[] = [],
): void {
  trackQuoteEvent("quote_contact_submit", {
    variant,
    procedures_count: proceduresCount,
    procedure_ids: procedureSlugs.join(","),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track potential price view (Variant B Step 4).
 *
 * @param variant - "control" or "preview"
 * @param baseTotal - Total base cost sum (integers, no decimal places)
 * @param potentialPrice - Calculated 80% price
 */
export function trackQuotePotentialPriceView(
  variant: QuoteABVariant,
  baseTotal: number,
  potentialPrice: number,
): void {
  trackQuoteEvent("quote_potential_price_view", {
    variant,
    base_total: Math.round(baseTotal),
    potential_price: Math.round(potentialPrice),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track final estimate CTA click (progression to photos step).
 */
export function trackQuoteFinalEstimateCTAClick(variant: QuoteABVariant): void {
  trackQuoteEvent("quote_final_estimate_cta_click", {
    variant,
    timestamp: new Date().toISOString(),
  });
}

export type QuoteViewportSnapshot = {
  scrollY: number;
  scrollHeight: number;
  innerHeight: number;
  visualViewportHeight: number;
  devicePixelRatio: number;
  userAgent: string;
  isIframe: boolean;
  isResumeMode: boolean;
  initialPath: string;
};

/**
 * One-shot snapshot of the viewport on first quote-funnel mount. Used to
 * diagnose Android/Samsung Internet dropoff where users land mid-page.
 */
export function trackQuoteViewportSnapshot(
  snapshot: QuoteViewportSnapshot,
): void {
  trackQuoteEvent("quote_viewport_snapshot", {
    ...snapshot,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track exposure to the quote-summary financing/price block. Fired once per
 * quote (see the dedupe in QuoteSummaryTreatment). Originally split by arm for
 * the financing-forward-v1 test; that test concluded (treatment won) so it is
 * now single-arm, but the event still measures financing-block exposure.
 *
 * `ctaPresent` and `context` were added 2026-09-13 after PR #1816 (photo-gate
 * hardwiring) removed the summary's apply CTA while `priceLocked` but left
 * this exposure firing unconditionally — collapsing the CTR read mechanically
 * (11-18% -> 3-5%) with no change in patient behavior. `ctaPresent` records
 * whether an apply control was actually rendered at fire time; `context`
 * distinguishes the summary fire from the eval-modal fire so CTR can be read
 * against exposures where a click was actually possible. Events emitted
 * before this cutover carry neither field.
 * NO PII — surface + rounded amounts only.
 */
export function trackFinancingForwardExposure(payload: {
  surface: "mobile" | "desktop";
  bookingSurface: boolean;
  monthlyPayment: number;
  total: number;
  ctaPresent: boolean;
  context: "summary" | "eval_modal";
}): void {
  trackQuoteEvent("financing_forward_exposure", {
    surface: payload.surface,
    booking_surface: payload.bookingSurface,
    monthly_payment: Math.round(payload.monthlyPayment),
    total: Math.round(payload.total),
    cta_present: payload.ctaPresent,
    context: payload.context,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a click on the financing prequalify CTA. INTENT signal ONLY — the
 * patient is handed off to the provider's off-platform flow; this is NOT a
 * booking and NOT an approval, so it must never be counted as a conversion.
 * A diagnostic of financing interest (now single-arm — financing-forward-v1
 * concluded). NO PII — provider, surface, rounded amounts only.
 */
export function trackFinancingApplyClick(payload: {
  provider: string;
  surface: "mobile" | "desktop";
  monthlyPayment: number;
  total: number;
}): void {
  trackQuoteEvent("financing_apply_click", {
    provider: payload.provider,
    surface: payload.surface,
    monthly_payment: Math.round(payload.monthlyPayment),
    total: Math.round(payload.total),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Returns true the first time it's called with `key` in this tab session,
 * false afterwards. Used to dedupe mount-effect analytics that would
 * otherwise re-fire on every step remount (back-navigation). Fails open: if
 * sessionStorage is blocked (private mode / partitioned iframe) the event
 * fires per mount, which is the pre-dedupe behavior.
 */
export function oncePerSession(key: string): boolean {
  try {
    const storageKey = `xt_once:${key}`;
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, "1");
    return true;
  } catch {
    return true;
  }
}

/**
 * Focused campaign mode (formerly the procedure-focus-v1 A/B; focused won and
 * now serves 100% of campaign-eligible traffic). Fired once per session when
 * the focused procedure step first mounts. NO PII: ids and counts only.
 */
export function trackQuoteFocusModeEntered(payload: {
  focusType: "single" | "family";
  focusIds: string[];
  focusTypesParam: string[];
  surface: "mobile" | "desktop";
  cardVersion?: string;
}): void {
  trackQuoteEvent("quote_focus_mode_entered", {
    variant: "focused",
    focus_type: payload.focusType,
    focus_ids: payload.focusIds.join(","),
    focus_types_param: payload.focusTypesParam.join(","),
    surface: payload.surface,
    card_version: payload.cardVersion ?? "signature-v2",
    timestamp: new Date().toISOString(),
  });
}

/**
 * Focus params were present in the URL but did not resolve to an eligible
 * shortlist (typo'd/stale ids, gender-filtered-out, too many, resume/shared
 * session). Campaign-URL health signal — a spike means a broken ad link.
 */
export function trackQuoteFocusFallback(payload: {
  focusTypesParam: string[];
  focusProceduresParam: string[];
  resolvedCount: number;
  surface: "mobile" | "desktop";
}): void {
  trackQuoteEvent("quote_focus_fallback", {
    focus_types_param: payload.focusTypesParam.join(","),
    focus_procedures_param: payload.focusProceduresParam.join(","),
    resolved_count: payload.resolvedCount,
    surface: payload.surface,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Before & afters lightbox opened in focused mode. Strong mid-funnel intent
 * signal — fires once per open (not per image viewed).
 */
export function trackQuoteFocusBaClicked(payload: {
  procedureId: string;
  imageCount: number;
}): void {
  trackQuoteEvent("quote_focus_ba_clicked", {
    procedure_id: payload.procedureId,
    image_count: payload.imageCount,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Booking-fee payment surfaces the journey events below are shared across:
 * the self-book funnel's `/id/<quoteId>` summary (`booking_link`, via
 * BookingPage.tsx), the campaign one-click `/action/book-direct/:token` page
 * (`book_direct`, via BookDirectPage.tsx), and the Send Estimate landing page
 * `/action/accept-estimate/:token` (`estimate_pay_on_land`, via
 * AcceptEstimatePage.tsx), which pays the same booking deposit inline right
 * on the estimate-review page — no navigation to /action/book (PR #1272,
 * "pay-on-land").
 */
export type BookingLinkType =
  | "booking_link"
  | "book_direct"
  | "estimate_pay_on_land";

/**
 * Track a Book Now CTA click on the quote-summary treatment. INTENT signal
 * ONLY — clicking Book Now does not create a booking (that's confirmed later
 * at payment). Fired once per click, before the pressed-breadcrumb mutation
 * and any payment path decision. NO PII — surface/flags/rounded price only.
 */
export function trackQuoteBookNowClicked(payload: {
  fromTrigger: boolean;
  isAdmin: boolean;
  prepared: boolean;
  bookingPriceCents: number;
}): void {
  trackQuoteEvent("quote_book_now_clicked", {
    surface: "quote_summary",
    from_trigger: payload.fromTrigger,
    is_admin: payload.isAdmin,
    prepared: payload.prepared,
    booking_price_cents: payload.bookingPriceCents,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track arrival on a booking-fee payment page. Fires on mount, independent of
 * the page's `/info` fetch resolving — a dead/expired link must still
 * register as an arrival. Deduped once per tab session via `oncePerSession`.
 * NEVER include the booking token (it's a credential) in the payload or in
 * the dedupe key.
 */
export function trackQuoteBookingPageView(linkType: BookingLinkType): void {
  trackQuoteEvent("quote_booking_page_view", {
    link_type: linkType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track booking-fee terms acceptance. Fires only on the transition to
 * checked (never on uncheck), deduped once per tab session so toggling the
 * checkbox back and forth can't spam the event.
 */
export function trackQuoteBookingTermsAccepted(
  linkType: BookingLinkType,
): void {
  trackQuoteEvent("quote_booking_terms_accepted", {
    link_type: linkType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track booking-fee payment start. INTENT signal ONLY — the patient has
 * begun the Stripe payment flow, not completed it. This is NEVER a booking;
 * a booking is only confirmed later via the Stripe webhook. NO PII — link
 * type + rounded amount only.
 */
export function trackQuoteBookingPaymentStarted(payload: {
  linkType: BookingLinkType;
  amountCents: number;
}): void {
  trackQuoteEvent("quote_booking_payment_started", {
    link_type: payload.linkType,
    amount_cents: Math.round(payload.amountCents),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a link-state resolution for a booking-fee landing page's `/info`
 * fetch — book_direct today (other BookingLinkType surfaces can adopt the
 * same event later). `quote_booking_page_view` fires on mount, BEFORE token
 * validation, so it can't distinguish a real visit from a dead/expired link;
 * this event fires once the server has actually classified the token. Fired
 * once per tab session. NO PII — link type + coarse state only.
 */
export function trackQuoteBookingLinkState(payload: {
  linkType: BookingLinkType;
  state: "ok" | "expired" | "rate_limited" | "feature_off" | "error";
}): void {
  trackQuoteEvent("quote_booking_link_state", {
    link_type: payload.linkType,
    state: payload.state,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a successful "Request a Callback" submission from an EXPIRED
 * book-direct link (BookDirectPage.tsx's expired-link recovery card — see
 * server/routes/book-direct.ts POST /request-callback). Fires ONLY on a
 * confirmed `{ ok: true }` response, deduped once per tab session via the
 * same `oncePerSession` helper the page already uses elsewhere. NO PII —
 * link type only.
 */
export function trackQuoteBookingCallbackRequested(payload: {
  linkType: BookingLinkType;
}): void {
  trackQuoteEvent("quote_booking_callback_requested", {
    link_type: payload.linkType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track the start of a photo-upload batch on the quote funnel's Photos step.
 * Fired once per upload-function invocation, not at each individual
 * file-picker call site. The two entry points batch at different
 * granularities, both pre-existing and unchanged by this instrumentation:
 * QuoteSummaryTreatment.tsx's hidden multi-file `<input>` calls
 * handlePhotoUpload once per whole OS-picker selection, so file_count there
 * reflects the real batch size. StepPhotos.tsx's antd `<Upload>` calls
 * handleUpload via `beforeUpload`, which rc-upload fires ONCE PER FILE even
 * for a multi-select — see the `pendingUploadCount` comment above handleUpload
 * — so file_count on the desktop surface is (almost) always 1; treat its
 * events as per-file, not per-selection. NO PII — file count only, never
 * filenames.
 */
export function trackQuotePhotoUploadStarted(payload: {
  fileCount: number;
}): void {
  trackQuoteEvent("quote_photo_upload_started", {
    file_count: payload.fileCount,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a photo-upload batch finishing with every file uploaded
 * successfully. Exists to answer "struggling to upload" vs "idle tab" for
 * the funnel's climbing Photos-step p90 time-on-step (341s→1038s over 5
 * reviews) — there was previously zero upload instrumentation.
 */
export function trackQuotePhotoUploadCompleted(payload: {
  fileCount: number;
  durationMs: number;
}): void {
  trackQuoteEvent("quote_photo_upload_completed", {
    file_count: payload.fileCount,
    duration_ms: Math.round(payload.durationMs),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track a photo-upload batch where at least one file failed. `errorBucket`
 * is intentionally coarse — `"http_<status>"` | `"network"` | `"other"` —
 * never a raw error message or filename (no PII, no leaking backend
 * internals). See `classifyUploadResponse`/`classifyUploadNetworkError`.
 */
export function trackQuotePhotoUploadFailed(payload: {
  fileCount: number;
  durationMs: number;
  errorBucket: string;
}): void {
  trackQuoteEvent("quote_photo_upload_failed", {
    file_count: payload.fileCount,
    duration_ms: Math.round(payload.durationMs),
    error_bucket: payload.errorBucket,
    timestamp: new Date().toISOString(),
  });
}

export type QuoteUploadFailure = { ok: false; errorBucket: string };
export type QuotePhotoUploadOutcome = { ok: true } | QuoteUploadFailure;

/**
 * Coarse, PII-free classification of a single file's upload response — used
 * by both StepPhotos.tsx (desktop antd Upload) and QuoteSummaryTreatment.tsx
 * (hidden multi-file input) so the two entry points bucket failures the same
 * way. Analytics-only: reads `response.ok`/`.status`, never the body, so it
 * never races or interferes with the caller's own `response.text()` read.
 */
export function classifyUploadResponse(
  response: Response,
): QuotePhotoUploadOutcome {
  if (response.ok) return { ok: true };
  return { ok: false, errorBucket: `http_${response.status}` };
}

/**
 * Coarse classification for a rejected upload fetch (never resolved to a
 * Response at all — offline, DNS, CORS, …). Browsers surface those as a
 * TypeError; anything else is unexpected, so it buckets as "other" rather
 * than guessing.
 */
export function classifyUploadNetworkError(
  error: unknown,
): QuoteUploadFailure {
  return {
    ok: false,
    errorBucket: error instanceof TypeError ? "network" : "other",
  };
}

/**
 * Declare gtag types for TypeScript
 */
declare global {
  interface Window {
    gtag?: (command: string, target: string, config?: any) => void;
  }
}
